import {
  Stack,
  Typography,
  Grid,
  Box,
  Theme,
  lighten,
  darken,
} from "@mui/material";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { useAppSystem } from "@/global/hooks/useAppSystem";
import { ReactNode } from "react";
import { CgCheck } from "react-icons/cg";
import { LoadingChip } from "@/shared/base/components/LoadingChip/LoadingChip";
import ArrowRightAltRoundedIcon from "@mui/icons-material/ArrowRightAltRounded";

interface Props {
  title?: string;
  description?: string;
  amount?: number | null;
  isSelected?: boolean;
  onSelect?: (ev: any) => void;
  hideButton?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  backgroundColor?: string;
}

export function CreditPackageItem(props: Props) {
  const { theme } = useAppSystem();
  const styles = useStyles(theme, props);
  const {
    title,
    description,
    amount,
    onSelect,
    isSelected,
    hideButton,
    isLoading,
    backgroundColor,
  } = props;

  return (
    <Box sx={styles.root} onClick={onSelect}>
      <Stack spacing={3} p={3} minHeight={370} flexGrow={1}>
        <Box>
          {title ? (
            <Typography
              variant="subtitle1"
              color={backgroundColor ? "white" : "inherit"}
            >
              {title}
            </Typography>
          ) : null}
          {amount ? (
            <Typography
              fontSize={"48px"}
              color={backgroundColor ? "white" : "text.primary"}
              fontWeight={700}
              lineHeight={1.3}
            >
              {getUSCurrency(amount).replace(".00", "")}
            </Typography>
          ) : null}
        </Box>

        {description ? (
          <Box>
            <Grid container spacing={1.5}>
              {description.split("<br/>").map((itm, idx) => {
                return (
                  <Grid key={idx} item xs={12}>
                    <IconListItem
                      key={idx}
                      color={backgroundColor ? "white" : "text.secondary"}
                    >
                      {itm}
                    </IconListItem>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null}

        <Box sx={{ flexGrow: 1 }} />

        {!hideButton ? (
          <Grid container>
            <Grid
              item
              xs={12}
              md={8}
              lg={6}
            >
              <LoadingChip
                disabled={props.isDisabled}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onSelect?.(ev);
                }}
                label={
                  <Stack direction={"row"} alignItems={"center"} spacing={1}>
                    <span
                      style={{ color: backgroundColor ? "white" : "black" }}
                    >
                      {"Buy Now"}
                    </span>{" "}
                    {/* generic to every team */}
                    <ArrowRightAltRoundedIcon
                      className="arrow-icon"
                      color={!isSelected ? "primary" : "inherit"}
                    />
                  </Stack>
                }
                isLoading={isLoading}
                clickable
                variant={isSelected ? "filled" : "outlined"}
                color={isSelected ? "primary" : "default"}
                size="large"
                sx={{
                  fontWeight: "600",
                  width: "100%",
                  height: "48px",
                  fontSize: "1rem",
                  backgroundColor: backgroundColor
                    ? darken(backgroundColor, 0.1)
                    : "white",
                }}
              />
            </Grid>
          </Grid>
        ) : null}
      </Stack>
    </Box>
  );
}

function IconListItem({
  children,
  color,
}: {
  children: ReactNode;
  color: string;
}) {
  return (
    <Stack direction={"row"} alignItems={"center"} spacing={1}>
      <Stack
        justifyContent={"center"}
        alignItems={"center"}
        width={"24px"}
        height={"24px"}
        bgcolor={"primary.main"}
        borderRadius={"50%"}
        color={"primary.contrastText"}
      >
        <CgCheck style={{ fontSize: "24px" }} />
      </Stack>

      <Typography
        variant="body2"
        color={color}
        dangerouslySetInnerHTML={{ __html: children as string }}
      />
    </Stack>
  );
}

function useStyles(theme: Theme, states: Props) {
  const { isSelected, isDisabled } = states;

  const primaryColor = theme.palette.primary.main;
  const dividerColor = theme.palette.divider;
  const outlineWidth = isSelected ? "2px" : "1px";
  const outlineColor = isSelected ? primaryColor : dividerColor;
  const hoverShadowColor = lighten(primaryColor, 0.8);

  return {
    root: {
      borderRadius: 1,
      transition: `background-color 0.3s ease, 
        transform 0.2s ease, 
        box-shadow 0.2s ease, 
        outline 0.2s ease`,
      cursor: !isDisabled ? "pointer" : "default",
      pointerEvents: !isDisabled ? "all" : "none",
      outline: `${outlineWidth} solid ${outlineColor}`,
      // opacity: isDisabled ? 0.8 : 1,
      backgroundColor: states.backgroundColor || "white",
      minHeight: "507px",
      height: "100%",
      position: "relative",
      display: "flex",
      flexDirection: "column",

      "& .arrow-icon": {
        transition: "all 0.2s ease-in-out",
      },

      "&:hover": {
        transform: !isDisabled ? "scale(1.03)" : "none",
        boxShadow: !isDisabled ? `0 0 0 6px ${hoverShadowColor}` : "none",

        "& .arrow-icon": {
          transform: "translateX(10px) scale(1.2)",
        },

        [theme.breakpoints.down("sm")]: {
          transform: "none",
        },
      },
    },
  };
}
